<!--eslint-disable vue/no-v-html-->
<template>
  <v-app-bar
    v-if="page.showToolbar"
    id="main-app-bar"
    class="nav-bar"
    :color="page.transparentToolbar ? 'transparent' : colors.bar"
    :class="page.transparentToolbar ? 'text-primary' : colors.barText"

    :flat="noScroll"
    style="overflow-y: hidden; transition: height 0.3s;"
  >
    <div
      class="nav-bar__content"
      :class="{ 'show-back-arrow': showBackArrow }"
    >
      <v-btn
        v-if="backTo && showBackArrow"
        icon
        variant="flat"
        :elevation="$vuetify.display.xs ? 1 : 0"
        class="mx-2 mt-1"
        :color="colors.bar"
        :class="`text-${colors.arrow}`"
        @click="$router.push(backTo)"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div v-else-if="showBackArrow" />
      <v-toolbar-title
        v-if="true"
        class="d-flex text-h5 align-center px-2"
        :class="$vuetify.display.smAndUp ? 'justify-center' : 'justify-start'"
        v-html="page.title"
      />
      <v-toolbar-title
        v-else
        class="px-2"
      >
        <div
          v-if="editingProjectMetadata"
          class="d-flex align-center justify-center"
          style="height: 100%;"
        >
          <v-textarea
            v-model="textFieldValue"
            style="max-width: 350px;"
            rows="1"
            hide-details
            class="ma-0 pa-0"
            placeholder="Add a title"
          >
            {{ page.title }}
          </v-textarea>
          <v-btn
            class="ml-1"
            color="shamrock"
            elevation="0"
            :variant="$vuetify.display.smAndUp ? 'text' : undefined"
            :icon="$vuetify.display.xs"
            @click="handleSaveChanges"
          >
            {{ $vuetify.display.smAndUp ? 'Save' : '' }}
            <v-icon :end="$vuetify.display.smAndUp">
              mdi-check-circle-outline
            </v-icon>
          </v-btn>
          <v-btn
            class="ml-1"
            color="error"
            elevation="0"
            :variant="$vuetify.display.smAndUp ? 'text' : undefined"
            :icon="$vuetify.display.xs"
            @click="handleDiscardChanges"
          >
            {{ $vuetify.display.smAndUp ? 'Discard' : '' }}
            <v-icon :end="$vuetify.display.smAndUp">
              mdi-close
            </v-icon>
          </v-btn>
          <!-- <v-btn
            class=""
            color="metal"
            elevation="0"
            variant="text"
            @click="toggleEditingProjectMetadata"
          >
            Discard changes
            <v-icon end>
              mdi-close-circle-outline
            </v-icon>
          </v-btn> -->
        </div>
        <div
          v-else
          class="d-flex align-center justify-center"
        >
          <span>{{ page.title }}</span>
          <v-tooltip
            location="end"
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                :icon="!editingProjectMetadata"
                class="ml-3 edit-button"
                color="metal"
                v-bind="activatorProps"
                @click="toggleEditingProjectMetadata()"
              >
                <v-icon>
                  mdi-pencil
                </v-icon>
              </v-btn>
            </template>
            Edit project title and description
          </v-tooltip>
          <v-tooltip location="right">
            <template #activator="{ props: activatorProps }">
              <v-btn
                icon
                color="error"
                v-bind="activatorProps"
                @click="$store.dispatch(page.deleteAction)"
              >
                <v-icon>
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
            Delete project
          </v-tooltip>
        </div>
      </v-toolbar-title>
      <div
        class="d-flex justify-end align-center"
      >
        <router-link
          v-if="page.showUserOptions && !user"
          to="/start"
        >
          <v-btn
            elevation="0"
            rounded="0"
            width="100"
            class="mx-2"
            :color="colors.bar"
          >
            Sign In
          </v-btn>
        </router-link>
        <div
          v-else
        >
          <UserSettings
            v-intro="'Click here to access the new Pathfinder 3.2 User Guide'"
            v-intro-step="1"
            v-intro-position="'left'"
            :small="$vuetify.display.smAndDown && $router.currentRoute.value.name === 'calculator'"
          />
        </div>
      </div>
    </div>
  </v-app-bar>
</template>

<script>
import { mapState, createNamespacedHelpers } from 'vuex'
import introJs from 'intro.js'
import UserSettings from '@/components/application/UserSettings'

const INTRO_COOKIE = 'help-intro-shown'
const hasIntroCookie = () =>
  document.cookie.split('; ').some(c => c.startsWith(`${INTRO_COOKIE}=`))
const setIntroCookie = () => {
  const expires = new Date(Date.now() + 7 * 864e5).toUTCString()
  document.cookie = `${INTRO_COOKIE}=1; expires=${expires}; path=/`
}

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapProjectGetters, mapMutations: mapProjectMutations } = createNamespacedHelpers('projects')

export default {
  name: 'ToolbarTop',
  components: {
    UserSettings
  },
  data () {
    return {
      noScroll: true,
      textFieldValue: ''
    }
  },
  computed: {
    ...mapState(['page']),
    ...mapAuthGetters(['user']),
    ...mapProjectGetters(['editingProjectMetadata']),
    showBackArrow () {
      return this.$vuetify.display.smAndUp || this.page.showBackArrowOnMobile
    },
    backTo () {
      if (this.page.backButtonPath) {
        return this.page.backButtonPath
      }
      const p = this.$route.path
      const i = p.lastIndexOf('/')
      if (i === -1) return null
      return p.substr(0, i)
    },
    colors () {
      const map = {
        transparent: { bar: 'transparent', barText: 'text-primary', arrow: 'primary' },
        white: { bar: 'white', barText: 'text-primary', arrow: 'primary' },
        light: { bar: 'leaf', barText: 'text-primary', arrow: 'primary' },
        dark: { bar: 'onyx', barText: 'text-white', arrow: 'white' }
      }
      return map[this.page.toolbar] || map.dark
    }
  },
  watch: {
    $route (route) {
      this.showIntro(route.name)
      setTimeout(() => {
        this.noScroll = document.body.scrollHeight <= document.body.clientHeight
      })
    },
    'page.title': function () {
      this.textFieldValue = this.page.title
    }
  },
  created () {
    this.textFieldValue = this.page.title
  },
  methods: {
    ...mapProjectMutations(['toggleEditingProjectMetadata']),
    showIntro (route) {
      const showIntroOnRoutes = ['project', 'calculator']
      if (!hasIntroCookie() && showIntroOnRoutes.includes(route)) {
        introJs()
          .setOptions({
            showStepNumbers: false,
            overlayOpacity: 0.4,
            hidePrev: true,
            hideNext: true,
            doneLabel: 'Let me try',
            skipLabel: 'Skip guide',
            disableInteraction: true,
            highlightClass: 'intro-action-on-dark'
          })
          .start()
        setIntroCookie()
      }
    },
    handleSaveChanges () {
      if (!this.textFieldValue) {
        this.$store.dispatch('showSnackbar', 'You must enter some text to save changes')
        return
      }
      this.$store.dispatch(this.page.editableTitleAction, this.textFieldValue)
      this.toggleEditingProjectMetadata()
    },
    handleDiscardChanges () {
      this.toggleEditingProjectMetadata()
      this.textFieldValue = this.page.title
    }
  }
}
</script>

<style lang="scss">
.nav-bar {
  .v-toolbar__content {
    padding: 8px;
  }
  .nav-bar__content {
    width: 100%;
    display: grid;
    // grid-template-columns: 130px 1fr 130px;
    grid-template-columns: 1fr 10vw;
    &.show-back-arrow {
      grid-template-columns: 10vw 1fr 10vw;
    }
  }
}
.intro-action-on-dark {
  background-color: rgba(255, 255, 255, 0.5) !important;
}
</style>
